import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SettlementExportStatus } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { ReactNode } from 'react';

import { createdBy } from '~components/Job/AuditDetails/utils';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { SettlementExportBasic } from '~hooks/useSettlementExports/models';
import isEnumValue from '~utils/isEnumValue';
import { getSettlementExportRendererLabel } from '~utils/settlements/settlement-export-utils';

const getSettlementExportBatchStatusInfo = (settlementExport: SettlementExportBasic) => {
  const statusInfo = {
    [SettlementExportStatus.COMPLETED]: {
      icon: <CheckCircleOutline color="success" fontSize="small" sx={{ mr: 1 }} />,
      statusText: settlementExport.completedAt
        ? dayjs.tz(settlementExport.completedAt).format('MMM DD')
        : t('approvals.driver_pay.settlements.settlement_export.statuses.completed'),
    },
    [SettlementExportStatus.FAILED]: {
      icon: <WarningAmber color="warning" fontSize="small" sx={{ mr: 1 }} />,
      statusText: t('approvals.driver_pay.settlements.settlement_export.statuses.failed'),
    },
    [SettlementExportStatus.PENDING]: {
      icon: (
        <LoadingSpinner isVisible loadingIndicatorSize={16} sx={{ mr: 1, flex: 0 }} />
      ),
      statusText: t(
        'approvals.driver_pay.settlements.settlement_export.statuses.pending',
      ),
    },
  } satisfies Record<SettlementExportStatus, { icon: ReactNode; statusText: string }>;

  // The API can send a status newer than this build's spec; render it as unknown
  // rather than crash the grid cell.
  if (!isEnumValue(SettlementExportStatus, settlementExport.status)) {
    return { icon: null, statusText: '-' };
  }

  return statusInfo[settlementExport.status];
};

interface SettlementExportBatchStatusProps {
  settlementExport: SettlementExportBasic;
  sx?: SxProps;
  labelSx?: SxProps;
}

export default function SettlementExportBatchStatus({
  settlementExport,
  sx,
  labelSx,
}: SettlementExportBatchStatusProps) {
  const { icon, statusText } = getSettlementExportBatchStatusInfo(settlementExport);
  const rendererLabel = getSettlementExportRendererLabel(settlementExport.rendererKey);
  const requestedByName = settlementExport.requestedBy
    ? createdBy(settlementExport.requestedBy)
    : null;

  return (
    <Box
      data-test-id="settlement-export-batch-status"
      sx={[{ alignItems: 'center', display: 'flex' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {icon}
      <Box>
        <Typography variant="body2" sx={labelSx}>
          {rendererLabel}
          {' • '}
          {statusText}
        </Typography>
        {requestedByName && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('approvals.driver_pay.settlements.settlement_export.requested_by', {
              name: requestedByName,
            })}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
